<script setup lang="ts">
    import { CardMenu } from '@/components/ui/card';
    import { columns } from '@/tables/columns/user-groups';
    import { Head } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import { useUrlSearch } from '@/composables/useUrlSearch';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateUserGroup from '@/components/user-groups/CreateUserGroup.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { BreadcrumbItem, NavItem } from '@/types';

    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.settings',
        },
        {
            title: 'menu.employees',
            href: route('v2.user-groups.index'),
        },
    ];

    const navItems: NavItem[] = [
        {
            title: 'companies.header_user_companies',
            href: route('v2.user-companies.index'),
            permissionLevel: Permission.COMPANIES_VIEW,
        },
        {
            title: 'user-groups.header_users_groups',
            href: route('v2.user-groups.index'),
            permissionLevel: Permission.COMPANIES_VIEW,
        },
    ];

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.USER_GROUPS],
        queryFn: getUserGroups,
    });

    const { extractAndCleanSearch } = useUrlSearch();
    const initialSearch = extractAndCleanSearch();

    async function getUserGroups() {
        const { data } = await axios.get(route('v2.user-groups.table'), {
            params: {
                search: initialSearch || undefined,
            },
        });
        return data.data;
    }
</script>

<template>
    <Head :title="$t('profile.profile_settings')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard>
            <DataTable
                :table-key="QueryKey.USER_GROUPS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :auto-url-search="true"
            >
                <template #title>
                    <CardMenu :items="navItems" />
                </template>
                <template #actions>
                    <DataTableToolbarActions name="user-groups">
                        <CreateUserGroup v-if="hasPermission(Permission.COMPANIES_CREATE)" />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
